import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    collection,
    getDocs,
    limit as firestoreLimit,
    onSnapshot,
    orderBy,
    query,
    where,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type {
    AllocationTimelineEvent,
    AllocationTimelineActor,
    AllocationTimelineEventSeverity,
    AllocationTimelineEventSource,
    AllocationTimelineEventType,
    AllocationTimelineCollaborator,
} from '@/features/Collaborators/Models';

export const allocationTimelineQueryKeys = {
    all: ['allocationTimeline', 'events'] as const,
    collaborator: (collaboratorId: string) =>
        [...allocationTimelineQueryKeys.all, 'collaborator', collaboratorId] as const,
};

export interface AllocationTimelineFilters {
    collaboratorId?: string | null;
    limit?: number;
}

interface SnapshotManager {
    unsub: (() => void) | null;
    listeners: number;
}

const snapshotManagers: Record<string, SnapshotManager> = {};

function getManager(key: string): SnapshotManager {
    if (!snapshotManagers[key]) {
        snapshotManagers[key] = { unsub: null, listeners: 0 };
    }
    return snapshotManagers[key];
}

function mapActor(raw: any): AllocationTimelineActor | null {
    if (!raw) {
        return null;
    }

    const id = raw.id ?? raw.actorId ?? null;
    const name = raw.name ?? raw.actorName ?? null;
    const avatar = raw.avatar ?? raw.actorAvatar ?? null;

    if (!id && !name && !avatar) {
        return null;
    }

    return { id, name, avatar };
}

function mapCollaborator(raw: any, fallbackId: string): AllocationTimelineCollaborator | null {
    if (!raw) {
        return {
            id: fallbackId,
            name: null,
            avatar: null,
        };
    }

    const id = raw.id ?? fallbackId ?? null;
    const name = raw.name ?? null;
    const avatar = raw.avatar ?? null;

    if (!id && !name && !avatar) {
        return null;
    }

    return { id, name, avatar };
}

function toDate(value: any): Date | null {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return value;
    }

    if (value instanceof Object && typeof value.toDate === 'function') {
        const parsed = value.toDate();
        return Number.isNaN(parsed?.getTime?.()) ? null : parsed;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function mapEvent(doc: any): AllocationTimelineEvent {
    const data = doc.data() ?? {};

    return {
        id: doc.id,
        collaboratorId: data.collaboratorId ?? null,
        allocationId: data.allocationId ?? null,
        type: (data.type ?? 'allocation_created') as AllocationTimelineEventType,
        actor: mapActor(data.actor),
        collaborator: mapCollaborator(data.collaborator, data.collaboratorId ?? doc.get('collaboratorId') ?? null),
        payload: data.payload ?? {},
        severity: (data.severity ?? 'info') as AllocationTimelineEventSeverity,
        source: (data.source ?? 'user') as AllocationTimelineEventSource,
        createdAt: toDate(data.createdAt),
    };
}

export interface UseAllocationTimelineQueryResult {
    data: AllocationTimelineEvent[];
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
    isFetching: boolean;
}

export function useAllocationTimelineQuery(filters: AllocationTimelineFilters = {}): UseAllocationTimelineQueryResult {
    const { collaboratorId = null, limit = 100 } = filters;
    const queryClient = useQueryClient();

    const isFiltered = Boolean(collaboratorId);

    const queryKey = useMemo(() => {
        if (isFiltered) {
            return [...allocationTimelineQueryKeys.collaborator(collaboratorId!), limit] as const;
        }
        return [...allocationTimelineQueryKeys.all, 'recent', limit] as const;
    }, [collaboratorId, isFiltered, limit]);

    const firestoreQuery = useMemo(() => {
        const baseRef = collection(db, 'allocationTimeline');
        if (isFiltered) {
            return query(
                baseRef,
                where('collaboratorId', '==', collaboratorId),
                orderBy('createdAt', 'desc'),
                firestoreLimit(limit),
            );
        }
        return query(baseRef, orderBy('createdAt', 'desc'), firestoreLimit(limit));
    }, [collaboratorId, isFiltered, limit]);

    const managerKey = useMemo(() => {
        return isFiltered ? `collaborator_${collaboratorId}_${limit}` : `all_${limit}`;
    }, [collaboratorId, isFiltered, limit]);

    const queryResult = useQuery<AllocationTimelineEvent[]>({
        queryKey,
        queryFn: async () => {
            const snap = await getDocs(firestoreQuery);
            return snap.docs.map(mapEvent);
        },
        initialData: () => {
            return queryClient.getQueryData(queryKey) as AllocationTimelineEvent[] | undefined;
        },
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
        keepPreviousData: true,
    });

    useEffect(() => {
        const manager = getManager(managerKey);
        manager.listeners += 1;

        if (!manager.unsub) {
            manager.unsub = onSnapshot(firestoreQuery, (snapshot) => {
                const events = snapshot.docs.map(mapEvent);
                queryClient.setQueryData(queryKey, events);
            });
        }

        return () => {
            manager.listeners -= 1;
            if (manager.listeners === 0 && manager.unsub) {
                manager.unsub();
                manager.unsub = null;
            }
        };
    }, [firestoreQuery, managerKey, queryClient, queryKey]);

    return {
        data: queryResult.data ?? [],
        isLoading: queryResult.isLoading,
        isError: queryResult.isError,
        error: (queryResult.error as Error) ?? null,
        isFetching: queryResult.isFetching,
    };
}
